import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { ExportMenu } from '../../components/ExportMenu';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/Table';
import { listBranches, listStockBalancesPage } from '../../data/fetchers';
import { fmtInt, fmtStockBreakdown } from '../../lib/format';
import { canSeeAllBranches, visibleBranchIds } from '../../lib/permissions';
import { useBranchScope } from '../../lib/useBranchScope';
import { useSession } from '../../stores/session';
import { StockProductSelect, useStockCatalog } from './stockCatalog';
import { StockHeader } from './StockHeader';
import { StockPagination, useStockCursor } from './StockPagination';

export function StockByBranchView() {
  const { scope, ready } = useBranchScope();
  const navigate = useNavigate();
  const user = useSession((s) => s.user);

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });

  const allowedIds = user
    ? visibleBranchIds(
        user,
        branches.map((b) => b.id),
      )
    : [];
  const visibleBranches = branches.filter((b) => allowedIds.includes(b.id));
  const selectedBranchId =
    scope.branchId ??
    (user && !canSeeAllBranches(user) ? (visibleBranches[0]?.id ?? '') : '');
  const [scannedProductId, setScannedProductId] = useState('');

  const queryScope = useMemo(
    () => ({
      branchId: selectedBranchId || null,
      allowedBranchIds: scope.allowedBranchIds,
    }),
    [selectedBranchId, scope.allowedBranchIds],
  );

  const pager = useStockCursor(JSON.stringify(queryScope));
  const stockQuery = useQuery({
    queryKey: ['stock', selectedBranchId, scope.allowedBranchIds, pager.cursor],
    queryFn: () =>
      listStockBalancesPage({
        branchId: selectedBranchId || undefined,
        after: pager.cursor,
        limit: 25,
      }),
    enabled: ready,
  });
  const stock = stockQuery.data?.rows ?? [];
  const catalog = useStockCatalog(stockQuery.data?.productIds ?? []);
  const products = catalog.products;
  const scanQuery = useQuery({
    queryKey: [
      'stock',
      'scan',
      selectedBranchId,
      scope.allowedBranchIds,
      scannedProductId,
    ],
    queryFn: () =>
      listStockBalancesPage({
        branchId: selectedBranchId || undefined,
        productIds: [scannedProductId],
        limit: 1,
      }),
    enabled: ready && !!scannedProductId,
  });

  const scannedProduct =
    products.find((product) => product.id === scannedProductId) ?? null;
  const scannedStockByBranch = useMemo(
    () =>
      new Map(
        (scanQuery.data?.rows ?? [])
          .filter((row) => row.productId === scannedProductId)
          .map((row) => [row.branchId, row.onHand]),
      ),
    [scanQuery.data, scannedProductId],
  );

  const nameOf = (id: string) => products.find((p) => p.id === id)?.name ?? id;
  const branchNameOf = (id: string) =>
    branches.find((b) => b.id === id)?.short ?? id;

  // Pivot: one row per product, a column per branch (when viewing all branches).
  const pivot = useMemo(() => {
    const byProduct = new Map<string, Record<string, number>>();
    for (const s of stock) {
      const r = byProduct.get(s.productId) ?? {};
      r[s.branchId] = s.onHand;
      byProduct.set(s.productId, r);
    }
    return [...byProduct.entries()].map(([productId, byBranch]) => {
      const row: Record<string, any> = {
        productId,
        name: nameOf(productId),
        byBranch,
      };
      for (const b of branches) {
        row[b.id] = byBranch[b.id] ?? 0;
      }
      return row;
    });
  }, [stock, products, branches]);

  const flat = useMemo(
    () =>
      stock.map((s) => ({
        productId: s.productId,
        name: nameOf(s.productId),
        onHand: s.onHand,
      })),
    [stock, products],
  );

  const flatExportData = useMemo(() => {
    return flat.map((item) => {
      const p = products.find((x) => x.id === item.productId);
      const onHandText = p
        ? fmtStockBreakdown(item.onHand, p)
        : String(item.onHand);
      return {
        name: item.name,
        onHandText,
      };
    });
  }, [flat, products]);

  const flatExportCols = [
    { key: 'name' as const, header: 'สินค้า' },
    { key: 'onHandText' as const, header: 'คงเหลือ' },
  ];

  const pivotExportData = useMemo(() => {
    return pivot.map((item) => {
      const row: Record<string, any> = { name: item.name };
      for (const b of branches) {
        row[b.id] = item.byBranch[b.id] ?? 0;
      }
      return row;
    });
  }, [pivot, branches]);

  const pivotExportCols = useMemo(
    () => [
      { key: 'name' as any, header: 'สินค้า' },
      ...branches.map((b) => ({ key: b.id as any, header: b.short })),
    ],
    [branches],
  );

  const pivotCols = useMemo<ColumnDef<Record<string, any>, any>[]>(
    () => [
      { accessorKey: 'name', header: 'สินค้า' },
      ...branches.map((b): ColumnDef<Record<string, any>, any> => ({
        id: b.id,
        header: b.short,
        cell: (c) => (
          <span className="num">
            {fmtInt(c.row.original.byBranch?.[b.id] ?? 0)}
          </span>
        ),
      })),
    ],
    [branches],
  );

  const flatCols: ColumnDef<
    { productId: string; name: string; onHand: number },
    unknown
  >[] = [
    { accessorKey: 'name', header: 'สินค้า' },
    {
      accessorKey: 'onHand',
      header: 'คงเหลือ',
      cell: (c) => {
        const p = products.find((x) => x.id === c.row.original.productId);
        return (
          <span className="num">
            {p
              ? fmtStockBreakdown(c.getValue<number>(), p)
              : fmtInt(c.getValue<number>())}
          </span>
        );
      },
    },
  ];

  return (
    <div className="bo-fade">
      <StockHeader
        title="สต๊อกแยกสาขา"
        subtitle={
          selectedBranchId
            ? 'คงเหลือในสาขาที่เลือก'
            : 'คงเหลือทุกสาขา (แยกคอลัมน์)'
        }
        actions={
          <div className="flex gap-2 items-center">
            <ExportMenu
              rows={selectedBranchId ? flatExportData : pivotExportData}
              cols={selectedBranchId ? flatExportCols : pivotExportCols}
              filename={
                selectedBranchId
                  ? `สต๊อกสินค้า - สาขา ${branchNameOf(selectedBranchId)}`
                  : 'สต๊อกสินค้าแยกทุกสาขา'
              }
              title={
                selectedBranchId
                  ? `รายงานสต๊อกสินค้า - สาขา ${branchNameOf(selectedBranchId)}`
                  : 'รายงานสต๊อกสินค้าแยกทุกสาขา'
              }
            />
            <Btn kind="primary" onClick={() => navigate('/stock/adjust')}>
              ปรับสต๊อก
            </Btn>
          </div>
        }
        status={{
          label: `${selectedBranchId ? flat.length : pivot.length} สินค้าในหน้านี้`,
          tone: stock.length > 0 ? 'ok' : 'neutral',
        }}
      />

      {/* Filter Bar */}
      <FilterBar>
        <div>
          <p className="block text-sm font-bold text-ink-2 mb-1.5">
            สแกนบาร์โค้ดเพื่อเช็กสต๊อก
          </p>
          <StockProductSelect
            catalog={catalog}
            value={scannedProductId}
            onChange={setScannedProductId}
            placeholder="ยิงบาร์โค้ด หรือพิมพ์ชื่อสินค้า..."
            className="max-w-2xl"
          />
          <p className="mt-1.5 text-xs text-ink-3">
            รองรับเครื่องยิงบาร์โค้ดและกล้องมือถือ —
            เมื่อพบสินค้าจะแสดงคงเหลือทันที
          </p>
        </div>
      </FilterBar>

      {scannedProduct && scanQuery.isSuccess && (
        <section
          data-testid="stock-scan-result"
          aria-live="polite"
          className="mb-6 overflow-hidden rounded-xl border border-primary/25 bg-surface shadow-sm"
        >
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line-2 bg-primary-light/40 px-5 py-4">
            <div>
              <div className="text-xs font-bold  text-primary">ผลการสแกน</div>
              <h2 className="mt-1 text-h3 font-black text-ink">
                {scannedProduct.name}
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {scannedProduct.units.map((unit) => (
                  <span
                    key={unit.id ?? `${unit.unit}-${unit.barcode ?? ''}`}
                    className="rounded-chip border border-line bg-surface px-2.5 py-1 text-xs text-ink-2"
                  >
                    {unit.barcode || 'ไม่มีบาร์โค้ด'} · {unit.unit}
                  </span>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setScannedProductId('')}
              className="rounded-btn px-3 py-2 text-sm font-bold text-ink-3 transition-colors hover:bg-surface hover:text-ink"
            >
              สแกนสินค้าอื่น
            </button>
          </div>

          {selectedBranchId ? (
            <div className="grid gap-2 px-5 py-5 sm:grid-cols-[1fr_auto] sm:items-center">
              <div>
                <div className="text-sm font-semibold text-ink-3">
                  คงเหลือพร้อมขาย · {branchNameOf(selectedBranchId)}
                </div>
                <div
                  data-testid="stock-scan-on-hand"
                  className="mt-1 text-2xl font-black text-primary num"
                >
                  {fmtStockBreakdown(
                    scannedStockByBranch.get(selectedBranchId) ?? 0,
                    scannedProduct,
                  )}
                </div>
              </div>
              <div className="text-sm text-ink-3">
                หน่วยฐาน: {scannedProduct.baseUnit}
              </div>
            </div>
          ) : (
            <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
              {visibleBranches.map((branch) => (
                <div
                  key={branch.id}
                  className="rounded-btn border border-line-2 bg-bg-warm/40 p-3"
                >
                  <div className="text-xs font-bold text-ink-3">
                    {branch.short}
                  </div>
                  <div className="mt-1 text-base font-black text-ink num">
                    {fmtStockBreakdown(
                      scannedStockByBranch.get(branch.id) ?? 0,
                      scannedProduct,
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {stockQuery.isError && (
        <p role="alert">
          โหลดสต๊อกไม่สำเร็จ{' '}
          <button onClick={() => void stockQuery.refetch()}>ลองใหม่</button>
        </p>
      )}
      {scannedProductId && scanQuery.isFetching && (
        <p role="status">กำลังโหลดสต๊อกสินค้า…</p>
      )}
      {scanQuery.isError && <p role="alert">โหลดสต๊อกสินค้าที่สแกนไม่สำเร็จ</p>}
      <p className="text-sm text-ink-3 mb-2">แสดงและส่งออกเฉพาะสินค้าหน้านี้</p>
      {selectedBranchId ? (
        <DataTable pageSize={25} columns={flatCols} data={flat} mobileCards />
      ) : (
        <DataTable pageSize={25} columns={pivotCols} data={pivot} mobileCards />
      )}
      <StockPagination
        pager={pager}
        hasMore={stockQuery.data?.hasMore}
        nextCursor={stockQuery.data?.nextCursor}
        pending={stockQuery.isFetching}
      />
    </div>
  );
}
